<template>
    <div :class="['view-generator-report-measurements-section', { 'view-generator-report-measurements-section--export-layout': isExportLayout }]">
        <template v-for="report in reports" :key="report.analysisType">
            <div
                v-if="report.analysisType"
                :class="[
                    'view-generator-report-measurements-section__item', 
                    { 'view-generator-report-measurements-section__item--export-layout': isExportLayout },
                ]"
            >
                <LoadingPanel :id="report.analysisType" :headline="t(tabs[report.analysisType]?.titleKey)">
                    <template #default>
                        <div class="view-generator-report-measurements-section__item__content">
                            <div 
                                v-if="!isExportLayout && MEASUREMENT_TYPES_REFERENCE_ALLOWED.includes(report.analysisType as MeasurementType)" 
                                class="view-generator-report-measurements-section__item__content__header"
                            >
                                <ReferenceMeasurementButton
                                    :report-number="reportNumber"
                                    :sub-report-name="t(tabs[report.analysisType]?.titleKey)"
                                    :component-id="generatorId"
                                    :is-reference-value="!!report.isCurrentReference"
                                    :analysis-type="report.analysisType"
                                    :can-be-set-as-reference="report.canBeSetAsReference"
                                />
                            </div>
                            <div class="view-generator-report-measurements-section__item__content__table">
                                <Component
                                    :is="tabs[report.analysisType].tableComponent"
                                    :data="{ status: report.status, reports: [report] }"
                                    :is-export-layout="isExportLayout"
                                    :view-type="MeasurementViewType.REPORT_DETAILS"
                                />
                            </div>
                        </div>
                    </template>

                    <template #context>
                        <div slot="context">
                            <div class="view-generator-report-measurements-section__item__header__context">
                                <ReferenceMeasurementButton
                                    v-if="isExportLayout && report.isCurrentReference && MEASUREMENT_TYPES_REFERENCE_ALLOWED.includes(report.analysisType as MeasurementType)"
                                    :report-number="reportNumber"
                                    :sub-report-name="t(tabs[report.analysisType]?.titleKey)"
                                    :component-id="generatorId"
                                    :is-reference-value="!!report.isCurrentReference"
                                    :analysis-type="report.analysisType"
                                    :can-be-set-as-reference="report.canBeSetAsReference"
                                />
                                <ColorBadge
                                    :text="t(StatusTypes.find((e) => e.value === report.status)?.labelKey ?? t(StatusTypes[ConditionStatus.None].labelKey))"
                                    :color="StatusTypes.find((e) => e.value === report.status)?.color ?? StatusTypes[ConditionStatus.None].color"
                                    small
                                />
                            </div>
                        </div>
                    </template>
                </LoadingPanel>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue'
import { computed, defineAsyncComponent, toRefs, watch } from 'vue'
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { useI18n } from 'vue-i18n'
import { MeasurementType, MeasurementTypes } from '@/configuration/measurements/measurement-types'
import { ConditionStatus, StatusTypes } from '@/configuration/status/condition-status'
import ColorBadge from '@/components/badge/ColorBadge.vue'
import { useGeneratorReportGetQuery } from '@/api/queries/reports/useGeneratorReportGetQuery'
import ReferenceMeasurementButton from '@/pages/reports/view-report-page/common/reference-measurement/ReferenceMeasurementButton.vue'
import { MEASUREMENT_TYPES_REFERENCE_ALLOWED } from '@/configuration/measurements/measurement-report-details'
import { MeasurementViewType } from '@/pages/generators/condition-status/sections/measurements/tabs/tables/MeasurementViewType'
import { useRoute, useRouter } from 'vue-router'
import { scrollToElement } from '@/utils/scroll-utils'

type TabItem = {
    titleKey: string
    key: number
    tableComponent: Component
}

type TabItems = {
    [key: number]: TabItem
}

const route = useRoute()
const router = useRouter()

const { t } = useI18n()

const props = defineProps<{
    reportNumber: string
    generatorId: string
    isExportLayout?: boolean
    reportType?: number
}>()

const { reportNumber, generatorId } = toRefs(props)

const { data } = useGeneratorReportGetQuery(reportNumber, generatorId)

const reports = computed(() => data.value?.reports ?? [])

const hasRecords = computed(() => !!data.value?.reports && Object.keys(data.value.reports).length > 0)

const processScroll = async () => {
    if (!route.query.focused) {
        return
    }

    await new Promise((resolve) => setTimeout(resolve, 500))
    scrollToElement(route.query.focused.toString())

    router.replace({
        query: {},
    })
}

watch(hasRecords, (newHasRecords) => {
    if (!newHasRecords) {
        return
    }

    processScroll()
}, { immediate: true })

const tabs: TabItems = {
    [MeasurementType.WindingResistance]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.WindingResistance)?.labelKey ?? '',
        key: MeasurementType.WindingResistance,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/WindingResistanceTable.vue')),
    },
    [MeasurementType.PoleImpedance]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.PoleImpedance)?.labelKey ?? '',
        key: MeasurementType.PoleImpedance,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/PoleImpedanceTable.vue')),
    },
    [MeasurementType.IsolationImpedanceStator]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceStator)?.labelKey ?? '',
        key: MeasurementType.IsolationImpedanceStator,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/IsolationImpedanceStatorTable.vue')),
    },
    [MeasurementType.IsolationImpedanceRotor]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceRotor)?.labelKey ?? '',
        key: MeasurementType.IsolationImpedanceRotor,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/IsolationImpedanceRotorTable.vue')),
    },
    [MeasurementType.LossFactor]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.LossFactor)?.labelKey ?? '',
        key: MeasurementType.LossFactor,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/LossFactorTable.vue')),
    },
    [MeasurementType.PartialDischarge]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.PartialDischarge)?.labelKey ?? '',
        key: MeasurementType.PartialDischarge,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/PartialDischargeTable.vue')),
    },
    [MeasurementType.VoltageTesting]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.VoltageTesting)?.labelKey ?? '',
        key: MeasurementType.VoltageTesting,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/VoltageTestingTable.vue')),
    },
    [MeasurementType.InductionTesting]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.InductionTesting)?.labelKey ?? '',
        key: MeasurementType.InductionTesting,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/InductionTestingTable.vue')),
    },
    [MeasurementType.HeatingTest]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.HeatingTest)?.labelKey ?? '',
        key: MeasurementType.HeatingTest,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/HeatingTestTable.vue')),
    },
    [MeasurementType.Vibrations]: {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.Vibrations)?.labelKey ?? '',
        key: MeasurementType.Vibrations,
        tableComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/tables/VibrationsTable.vue')),
    },
}
</script>

<style scoped lang="scss">
.view-generator-report-measurements-section {
    display: flex;
    flex-direction: column;
    gap: $spacing-s $spacing-m;

    &__item {
        width: 100%;

        &--export-layout {
            break-inside: avoid;
        }
        
        &__header {
            &__context {
                display: flex;
                align-items: center;
                gap: $spacing-s;
            }
        }

        &__content {
            margin: -($spacing-s);
            
            &__header {
                display: flex;
                padding: $spacing-s;
            }
            
            &__table {
                overflow: auto;
                border-top: 1px solid $pbl-divider;
            }
        }
    }
}

.laska {
    display: flex;
    align-items: center;
    gap: $spacing-s;
}
</style>